/**
 * FairwayBid – Nachweisfelder für Händler und Hersteller
 *
 * Auf der Startseite steht: "Bei Händlern und Herstellern prüfen wir einmalig
 * Handelsregister und USt-IdNr." Damit das keine Behauptung bleibt, müssen die
 * Angaben bei der Registrierung erhoben werden – hier stehen sie.
 *
 * Warum im Code und nicht in der Console: Erweiterte Daten sind bei Sharetribe
 * schemalos. Die Console-Feldliste steuert nur, welche Eingaben das Portal
 * zeigt; gespeichert wird auch ohne sie. Trägt jemand später ein Feld mit
 * demselben Schlüssel in der Console nach, gewinnt die Console (siehe
 * mitFairwayBidFeldern) – doppelte Felder kann es also nicht geben.
 *
 * Ablage: "private". Diese Angaben brauchen wir für die Freischaltung, nicht
 * die Golfanlagen für den Angebotsvergleich. Sie stehen damit im Konto und in
 * der Console, aber in keinem öffentlichen Profil.
 *
 * Bestehende Händlerkonten bleiben unberührt: Die Felder erscheinen dort im
 * Profil und können nachgetragen werden – niemand wird ausgesperrt.
 */

/** Der Nutzertyp der anbietenden Seite (die Golfanlagen sind "customer"). */
export const HAENDLER_USER_TYPE = 'provider';

/**
 * Pflicht oder nicht.
 *
 * Die USt-IdNr. ist Pflicht: Jeder, der europaweit B2B verkauft, hat eine, und
 * sie ist die einzige Angabe, die sich maschinell prüfen lässt (MIAS/VIES).
 *
 * Handelsregisternummer und Registergericht sind es bewusst nicht. Ein
 * Einzelunternehmen mit Gewerbeanmeldung steht in keinem Handelsregister, und
 * in den Niederlanden oder Frankreich heißt das Register anders. Ein
 * Pflichtfeld würde diese Händler entweder aussperren oder zu einem
 * "nicht eingetragen" im Feld zwingen – beides ist schlechter als eine
 * ehrliche Lücke, die bei der Prüfung auffällt.
 *
 * Soll doch alles Pflicht werden: hier auf true stellen, sonst nichts.
 */
export const HANDELSREGISTER_PFLICHT = false;

const nurHaendler = { limitToUserTypeIds: true, userTypeIds: [HAENDLER_USER_TYPE] };

export const FWB_USER_FIELDS = [
  {
    key: 'ust_id',
    scope: 'private',
    schemaType: 'shortText',
    helpText:
      'Wir prüfen sie vor der Freischaltung. Sie erscheint nicht in Ihrem öffentlichen Profil.',
    showConfig: {
      label: 'USt-IdNr.',
      displayInProfile: false,
    },
    saveConfig: {
      label: 'Umsatzsteuer-Identifikationsnummer',
      placeholderMessage: 'z. B. DE123456789',
      isRequired: true,
      requiredMessage: 'Bitte geben Sie Ihre USt-IdNr. an.',
      displayInSignUp: true,
    },
    userTypeConfig: nurHaendler,
  },
  {
    key: 'handelsregister',
    scope: 'private',
    schemaType: 'shortText',
    helpText:
      'Falls Sie eingetragen sind. Einzelunternehmen ohne Registereintrag lassen das Feld leer.',
    showConfig: {
      label: 'Handelsregisternummer',
      displayInProfile: false,
    },
    saveConfig: {
      label: 'Handelsregisternummer',
      placeholderMessage: 'z. B. HRB 12345',
      isRequired: HANDELSREGISTER_PFLICHT,
      requiredMessage: 'Bitte geben Sie Ihre Handelsregisternummer an.',
      displayInSignUp: true,
    },
    userTypeConfig: nurHaendler,
  },
  {
    key: 'registergericht',
    scope: 'private',
    schemaType: 'shortText',
    helpText: 'Das Gericht, das den Eintrag führt.',
    showConfig: {
      label: 'Registergericht',
      displayInProfile: false,
    },
    saveConfig: {
      label: 'Registergericht',
      placeholderMessage: 'z. B. Amtsgericht Friedberg',
      isRequired: HANDELSREGISTER_PFLICHT,
      requiredMessage: 'Bitte geben Sie das Registergericht an.',
      displayInSignUp: true,
    },
    userTypeConfig: nurHaendler,
  },
];

/**
 * Hängt die FairwayBid-Felder hinten an die Felder aus der Console an –
 * aber nur die, deren Schlüssel dort noch nicht vorkommt. Damit gewinnt immer
 * die Console, sobald jemand dasselbe Feld dort anlegt.
 */
export const mitFairwayBidFeldern = (hostedUserFields = []) => {
  const vorhanden = new Set(hostedUserFields.map(f => f?.key));
  return [...hostedUserFields, ...FWB_USER_FIELDS.filter(f => !vorhanden.has(f.key))];
};
